import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { GlassTitle, useEngineeringUi } from 'components/WellPages/Engineering/Utils/EngineeringStyling'
import { numberWithCommasDecimals, numberWithCommasMaxDecimals } from 'utils/stringFunctions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Icon as Iconify } from '@iconify/react'
import useUnits, { UNITS_FOR } from 'components/common/hooks/useUnits'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ClearIcon from '@mui/icons-material/Close'
import DataConfigFormItem from './DataConfigFormItem'
import DropDownPicker from 'components/common/DropDownPicker'
import RibbonDropdownMenu from 'components/common/Ribbon/RibbonComponents/RibbonDropdownMenu'
import { normalizeDropDownOptions } from 'components/WellPages/Engineering/Utils/EngineeringParamDefs'
import { toHexColor } from 'components/ThreeDeeView/Utils/ThreeDeeChartUtils'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const getToggleSx = (value) => {
  if (value !== 'PASS' && value !== 'FAIL') return {}

  const getTextColor = (value) => {
    return 'inherit'
  }

  const getBackgroundColor = (value) => {
    return 'transparent'
  }

  return {
    '&.Mui-selected': {
      color: getTextColor(value),
      borderColor: getTextColor(value),
      backgroundColor: getBackgroundColor(value),
    },
  }
}

const clampNumber = (value, minValue, maxValue) => {
  let nextValue = value
  if (typeof minValue === 'number') nextValue = Math.max(minValue, nextValue)
  if (typeof maxValue === 'number') nextValue = Math.min(maxValue, nextValue)
  return nextValue
}

export const formatDateWithRelativeTime = (dateString) => {
  if (!dateString) {
    return ''
  }

  const date = new Date(dateString)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const formattedDate = date.toLocaleString('default', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })

  const diffMs = date.getTime() - Date.now()
  const diffMinutes = Math.round(diffMs / (1000 * 60))
  const diffHours = Math.round(diffMinutes / 60)
  const diffDays = Math.round(diffHours / 24)

  let relative

  const rtf = new Intl.RelativeTimeFormat('en', {
    numeric: 'auto',
    style: 'short',
  })

  if (Math.abs(diffMinutes) < 60) {
    relative = rtf.format(diffMinutes, 'minute')
  } else if (Math.abs(diffHours) < 24) {
    relative = rtf.format(diffHours, 'hour')
  } else if (Math.abs(diffDays) < 7) {
    relative = rtf.format(diffDays, 'day')
  } else if (Math.abs(diffDays) < 30) {
    relative = rtf.format(Math.round(diffDays / 7), 'week')
  } else if (Math.abs(diffDays) < 365) {
    relative = rtf.format(Math.round(diffDays / 30), 'month')
  } else {
    relative = rtf.format(Math.round(diffDays / 365), 'year')
  }

  return `${formattedDate} (${relative})`
}

const formatDisplayValue = (def) => {
  const valueType = def?.type || def?.valueType || 'text'
  const currentValue = def?.value

  if (valueType === 'numeric') {
    if (currentValue === null || currentValue === undefined || currentValue === '') return ''

    const precision = typeof def?.precision === 'number' ? def.precision : 2

    if (def?.readOnly) {
      return numberWithCommasDecimals(currentValue, precision)
    }

    return numberWithCommasMaxDecimals(currentValue, precision)
  }

  if (valueType === 'boolean') return currentValue
  if (valueType === 'time') {
    return formatDateWithRelativeTime(currentValue)
  }

  return currentValue ?? ''
}

const coerceInputValue = (def, raw) => {
  const valueType = def?.type || def?.valueType || 'text'

  if (valueType === 'boolean') return raw

  if (valueType === 'numeric') {
    const rawText = String(raw ?? '').trim()
    if (rawText === '') return 0

    const parsed = Number(rawText)
    if (Number.isNaN(parsed)) return 0

    const precision = typeof def?.precision === 'number' ? def.precision : 2
    const rounded = Number(parsed.toFixed(precision))

    return clampNumber(rounded, def?.minValue, def?.maxValue)
  }

  return raw
}

const DataConfigParameterDisplay = ({
  defs,
  onChange,
  title = '',
  withSurface = true,
  icon = null,
  numCols = 1,
  labelWidth = 125,
  unitWidth = 90,
  editorMaxWidth = 340,
  minWidth = 0,
  display = 'grid',
  segments = null,
  disabledParamValue = false,
  borderColor = null,
  allowEmptyDropdowns = false,

  collapsible = false,
  defaultCollapsed = false,
}) => {
  const ui = useEngineeringUi(true)
  const { getUnitsText } = useUnits()

  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const [localDefs, setLocalDefs] = useState([])
  const [dropdownAnchor, setDropdownAnchor] = useState(null)
  const [edit, setEdit] = useState(null)

  const { getTextColor, getBackColor, theme } = useInnovaTheme()

  const unitsWidth = unitWidth
  const normalizedNumCols = Math.max(1, Number(numCols) || 1)

  useEffect(() => {
    if (edit) return

    if (!Array.isArray(defs)) {
      setLocalDefs([])
      return
    }

    setLocalDefs(defs.map((d) => ({ ...d })))
  }, [defs, edit])

  const visibleDefinitions = useMemo(() => {
    if (!Array.isArray(localDefs)) return []
    return localDefs.filter((d) => {
      if (!d) return false

      return true
    })
  }, [localDefs])

  const segmentedGroups = useMemo(() => {
    const groups = []
    let current = null

    for (const def of visibleDefinitions) {
      const key = def?.segment ?? '__default__'
      if (!current || current.key !== key) {
        current = { key, defs: [] }
        groups.push(current)
      }
      current.defs.push(def)
    }

    return groups
  }, [visibleDefinitions])

  const resolveSegment = useCallback(
    (key) => {
      const override = segments?.[key] ?? {}
      return {
        numCols: Math.max(1, Number(override.numCols) || normalizedNumCols),
        labelWidth: override.labelWidth ?? labelWidth,
        display: override.display ?? display,
      }
    },
    [segments, normalizedNumCols, labelWidth, display],
  )

  const segmentHasUnits = useCallback(
    (groupDefs) =>
      groupDefs.some((d) => {
        if (!d?.units) return false
        return getUnitsText(UNITS_FOR[d.units]) !== '-'
      }),
    [getUnitsText],
  )

  const setLocalValue = useCallback((id, value) => {
    setLocalDefs((prev) => prev.map((d) => (d?.id === id ? { ...d, value } : d)))
  }, [])

  const commit = useCallback(
    (def, rawDraft) => {
      const nextValue = coerceInputValue(def, rawDraft)
      setLocalValue(def.id, nextValue)

      if (typeof onChange === 'function') {
        const setterResult = typeof def.valueSetter === 'function' ? def.valueSetter(nextValue) : null
        if (Object.keys(setterResult ?? {}).length > 0) {
          for (const [key, val] of Object.entries(setterResult)) {
            onChange({ ...def, tag: key, value: val })
          }

          return
        }

        onChange({ ...def, value: nextValue })
      }
    },
    [onChange, setLocalValue],
  )

  const startEdit = useCallback((def) => {
    const valueType = def?.type || def?.valueType || 'text'
    const originalValue = def?.value

    let initialDraft = ''

    if (originalValue !== null && originalValue !== undefined) {
      if (valueType === 'numeric') {
        initialDraft = coerceInputValue(def, originalValue).toString()
      } else {
        initialDraft = String(originalValue)
      }
    }

    setEdit({
      id: def.id,
      def,
      valueType,
      originalValue,
      initialDraft,
      draft: initialDraft,
      didEdit: false,
    })
  }, [])

  const cancelEdit = useCallback(() => {
    setEdit((prev) => {
      if (!prev) return null
      setLocalValue(prev.id, prev.originalValue)
      return null
    })
  }, [setLocalValue])

  const updateDraft = useCallback((nextDraft) => {
    setEdit((prev) => {
      if (!prev) return prev
      return { ...prev, draft: nextDraft, didEdit: true }
    })
  }, [])

  const commitEdit = useCallback(() => {
    if (!edit) return

    const { def, draft, originalValue, valueType, didEdit } = edit

    if (!didEdit) {
      commit(def, originalValue)
      setEdit(null)
      return
    }

    let nextValue = draft

    if (valueType === 'numeric') {
      if (draft === '') {
        nextValue = ''
      } else {
        const parsed = Number(draft)
        nextValue = Number.isNaN(parsed) ? originalValue : parsed
      }
    }

    commit(def, nextValue)
    setEdit(null)
  }, [edit, commit])

  const renderValueCell = (def) => {
    if (typeof def?.render === 'function') {
      return def.render(def)
    }

    const valueType = def?.type || 'text'
    const id = def?.id
    const isEditingThis = id != null && edit?.id === id

    if (valueType === 'boolean') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 34 }}>
          <Checkbox
            size='small'
            checked={Boolean(def?.value)}
            disabled={
              def?.readOnly || (typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled)
            }
            onChange={(e) => {
              const next = e.target.checked
              setLocalValue(def.id, next)
              if (typeof onChange === 'function') onChange({ ...def, value: next })
            }}
            sx={{
              p: 0.25,
              color: ui.defaultTextColor,

              ...(def?.readOnly && {
                '&.Mui-disabled': {
                  color: ui.defaultTextColor,
                  opacity: 1,
                },
              }),
            }}
          />
        </Box>
      )
    }

    if (valueType === 'color') {
      const value = toHexColor(def?.value)

      const isDisabled = typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            maxWidth: editorMaxWidth,
          }}>
          <Box
            component='input'
            type='color'
            value={value}
            disabled={isDisabled}
            onChange={(e) => {
              const nextValue = e.target.value

              setLocalValue(def.id, nextValue)

              if (typeof onChange === 'function') {
                onChange({
                  ...def,
                  value: nextValue,
                })
              }
            }}
            sx={{
              width: 40,
              height: 40,
              p: 0,
              border: 'none',
              background: 'transparent',
              cursor: isDisabled ? 'default' : 'pointer',

              '&::-webkit-color-swatch-wrapper': {
                padding: 0,
              },

              '&::-webkit-color-swatch': {
                border: `1px solid ${theme === 'dark' ? '#2f3a45' : '#d0d7de'}`,
                borderRadius: '4px',
              },

              '&::-moz-color-swatch': {
                border: `1px solid ${theme === 'dark' ? '#2f3a45' : '#d0d7de'}`,
                borderRadius: '4px',
              },
            }}
          />

          <TextField
            size='small'
            value={value}
            disabled={isDisabled}
            placeholder='#000000'
            onChange={(e) => {
              const nextValue = e.target.value

              setLocalValue(def.id, nextValue)

              // Only send valid hex colors upstream
              if (/^#[0-9A-Fa-f]{6}$/.test(nextValue)) {
                if (typeof onChange === 'function') {
                  onChange({
                    ...def,
                    value: nextValue,
                  })
                }
              }
            }}
            sx={{
              ...ui.input,
              flex: 1,
              '& .MuiInputBase-input': {
                fontSize: '0.95rem',
                color: getTextColor(),
              },
            }}
          />
        </Box>
      )
    }

    if (valueType === 'date') {
      const toInputValue = (raw) => {
        if (typeof raw !== 'string') return ''
        return raw.slice(0, 10)
      }

      const value = def?.value
      const inputValue = isEditingThis ? edit.draft : toInputValue(value)

      return (
        <TextField
          fullWidth
          size='small'
          type='date'
          value={inputValue}
          InputLabelProps={{ shrink: true }}
          inputProps={{ readOnly: def?.readOnly }}
          disabled={typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled}
          onFocus={() => {
            setEdit({
              id: def.id,
              def,
              valueType,
              originalValue: value,
              initialDraft: toInputValue(value),
              draft: toInputValue(value),
              didEdit: false,
            })
          }}
          onChange={(e) => updateDraft(e.target.value)}
          onBlur={() => {
            if (!edit) return

            const { draft, originalValue, didEdit } = edit

            if (!didEdit) {
              commit(def, originalValue)
            } else {
              commit(def, draft)
            }

            setEdit(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancelEdit()
            }

            if (e.key === 'Enter' && edit) {
              e.preventDefault()
              commit(def, edit.draft)
              setEdit(null)
            }
          }}
          sx={{
            ...ui.input,
            '& .MuiInputBase-input': {
              fontSize: '0.95rem',
              color: getTextColor(),
            },
          }}
        />
      )
    }

    if (valueType === 'dateTime') {
      const toInputValue = (raw) => {
        if (!raw || typeof raw !== 'string') return ''
        const cleaned = raw.replace(/Z$/, '')
        if (cleaned.length < 10) return ''
        return cleaned.slice(0, 16)
      }

      const value = def?.value

      const inputValue = isEditingThis ? edit.draft : toInputValue(value)

      return (
        <TextField
          fullWidth
          size='small'
          type='datetime-local'
          value={inputValue}
          InputLabelProps={{ shrink: true }}
          inputProps={{ readOnly: def?.readOnly }}
          disabled={typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled}
          onFocus={() => {
            setEdit({
              id: def.id,
              def,
              valueType,
              originalValue: value,
              initialDraft: toInputValue(value),
              draft: toInputValue(value),
              didEdit: false,
            })
          }}
          onChange={(e) => updateDraft(e.target.value)}
          onBlur={() => {
            if (!edit) return
            const { draft, originalValue, didEdit } = edit
            if (!didEdit) {
              commit(def, originalValue)
              setEdit(null)
              return
            }

            const iso = draft ? `${draft}:00.000` : ''
            commit(def, iso)
            setEdit(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancelEdit()
            }
            if (e.key === 'Enter' && edit) {
              e.preventDefault()
              const iso = edit.draft ? `${edit.draft}:00.000` : ''
              commit(def, iso)
              setEdit(null)
            }
          }}
          sx={{
            ...ui.input,
            '& .MuiInputBase-input': { fontSize: '0.95rem', color: getTextColor() },
          }}
        />
      )
    }

    if (valueType === 'timeOnly') {
      const value = def?.value
      const inputValue = isEditingThis ? edit.draft : (value ?? '')

      const isValidTime = (time) => {
        if (typeof time !== 'string') return false

        return /^([01]\d|2[0-3]):[0-5]\d$|^24:00$/.test(time)
      }

      return (
        <TextField
          fullWidth
          size='small'
          type='text'
          value={inputValue}
          placeholder='00:00'
          inputProps={{
            readOnly: def?.readOnly,
            maxLength: 5,
          }}
          disabled={typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled}
          onFocus={() => {
            setEdit({
              id: def.id,
              def,
              valueType,
              originalValue: value,
              initialDraft: value ?? '',
              draft: value ?? '',
              didEdit: false,
            })
          }}
          onChange={(e) => {
            let newValue = e.target.value

            // Only allow digits and :
            newValue = newValue.replace(/[^\d:]/g, '')

            if (newValue.length > 5) {
              newValue = newValue.slice(0, 5)
            }

            updateDraft(newValue)
          }}
          onBlur={() => {
            if (!edit) return

            const { draft, originalValue, didEdit } = edit

            if (!didEdit) {
              commit(def, originalValue)
            } else if (isValidTime(draft)) {
              commit(def, draft)
            } else {
              commit(def, originalValue)
            }

            setEdit(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancelEdit()
            }

            if (e.key === 'Enter' && edit) {
              e.preventDefault()

              if (isValidTime(edit.draft)) {
                commit(def, edit.draft)
                setEdit(null)
              }
            }
          }}
          sx={{
            ...ui.input,
            '& .MuiInputBase-input': {
              fontSize: '0.95rem',
              color: getTextColor(),
            },
          }}
        />
      )
    }

    if (valueType === 'dropDown') {
      const options = normalizeDropDownOptions(def)
      let value = def?.value ?? ''
      if (!options.some((o) => o.value === value)) value = ''

      return (
        <DropDownPicker
          width={def?.width || 200}
          value={value}
          disabled={typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled}
          onChange={(newSelection) => {
            setLocalValue(def.id, newSelection)
            if (typeof onChange === 'function') onChange({ ...def, value: newSelection })
          }}
          listOptions={options?.length ? options : [{}]}
        />
      )
    }

    if (valueType === 'dropDownWithSearch') {
      const options = normalizeDropDownOptions(def)
      if (allowEmptyDropdowns && !options.some((o) => o.value === '')) {
        options.unshift({ label: 'None', value: '' })
      }
      let value = def?.value ?? ''
      if (!options.some((o) => o.value === value)) value = ''
      const selectedOption = options.find((o) => o.value === value)
      const isDisabled = typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled
      const isOpenForThis = Boolean(def.id !== null && def.id !== undefined && dropdownAnchor?.id === def.id)

      const menuItems = options.map((opt) => ({
        type: 'action',
        key: opt.value,
        label: opt.label ?? opt.value,
        value: opt.value,
        onClick: (val) => {
          setLocalValue(def.id, val)
          if (typeof onChange === 'function') onChange({ ...def, value: val })
        },
      }))

      return (
        <>
          <TextField
            fullWidth
            size='small'
            multiline
            maxRows={6}
            value={selectedOption?.label ?? selectedOption?.value ?? ''}
            disabled={isDisabled}
            inputProps={{ readOnly: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
            }}
            onClick={(e) => {
              if (isDisabled) return
              setDropdownAnchor({ id: def.id, anchorEl: e.currentTarget })
            }}
            sx={{
              width: def?.width ?? '100%',
              maxWidth: def?.width ?? editorMaxWidth,
              '& .MuiInputBase-root': {
                // height: 40,
                minHeight: 40,
                color: getTextColor(),
                backgroundColor: getBackColor(),
                cursor: isDisabled ? 'default' : 'pointer',
              },
              '& .MuiInputBase-input': {
                fontSize: '0.95rem',
                color: getTextColor(),
                cursor: isDisabled ? 'default' : 'pointer',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: `${getTextColor()} !important`,
                opacity: '0.8 !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDisabled ? undefined : '#429CEB',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#429CEB',
                boxShadow: '0 0 6px rgba(66,156,235,0.3)',
              },
            }}
          />
          <RibbonDropdownMenu
            anchorEl={isOpenForThis ? dropdownAnchor.anchorEl : null}
            open={Boolean(isOpenForThis)}
            onClose={() => setDropdownAnchor(null)}
            items={menuItems}
            searchable
            searchPlaceholder='Search...'
            minWidth={dropdownAnchor?.anchorEl?.offsetWidth ?? 220}
          />
        </>
      )
    }

    if (valueType === 'toggle') {
      const options = normalizeDropDownOptions(def)
      const value = def?.value ?? ''

      return (
        <ToggleButtonGroup
          key={def?.tag}
          exclusive
          size='small'
          value={value}
          disabled={typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled}
          onChange={(_, newValue) => {
            if (newValue === null) return
            setLocalValue(def.id, newValue)
            if (typeof onChange === 'function') onChange({ ...def, value: newValue })
          }}
          sx={{ flexWrap: 'wrap' }}>
          {options.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} sx={getToggleSx(opt.value)}>
              {opt.label ?? opt.value}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )
    }

    if (valueType === 'longstring') {
      return (
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          size='small'
          value={def?.readOnly ? formatDisplayValue(def) : isEditingThis ? edit.draft : formatDisplayValue(def)}
          onFocus={() => startEdit(def)}
          onClick={() => {
            if (!isEditingThis) startEdit(def)
          }}
          disabled={typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled}
          onChange={(e) => updateDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancelEdit()
            }
          }}
          onBlur={() => commitEdit()}
          error={Boolean(def?.error)}
          helperText={def?.helperText}
          sx={{
            ...ui.input,
            '& .MuiInputBase-input': { fontSize: '0.95rem', color: getTextColor(), width: '100%' },
          }}
        />
      )
    }

    return (
      <TextField
        fullWidth
        size='small'
        multiline
        maxRows={3}
        value={isEditingThis ? edit.draft : formatDisplayValue(def)}
        disabled={typeof def?.disabled === 'function' ? def.disabled(disabledParamValue) : def.disabled}
        inputProps={{ readOnly: def?.readOnly }}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton
                size='small'
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  updateDraft('')
                }}
                sx={{
                  color: '#ff6b6b',
                  '&:hover': { color: '#ff3b3b' },
                  visibility: !isEditingThis || !edit?.draft ? 'hidden' : 'visible',
                }}>
                {def?.readOnly !== true && <ClearIcon fontSize='small' />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        onFocus={(e) => {
          if (def?.readOnly) return

          startEdit(def)
          requestAnimationFrame(() => e.target.select?.())
        }}
        onClick={(e) => {
          if (def?.readOnly || isEditingThis) return

          startEdit(def)
          requestAnimationFrame(() => e.target.select?.())
        }}
        onChange={(e) => updateDraft(e.target.value)}
        onKeyDown={(e) => {
          if (def?.readOnly) return

          if (!isEditingThis) {
            if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
              e.preventDefault()
              const nextDraft = e.key.length === 1 ? e.key : ''
              setEdit({
                id: def.id,
                def,
                valueType: def?.type || def?.valueType || 'text',
                originalValue: def?.value,
                initialDraft: nextDraft,
                draft: nextDraft,
                didEdit: true,
              })
              return
            }
            if (e.key === 'Enter') {
              e.preventDefault()
              startEdit(def)
              return
            }
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            commitEdit()
            return
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            cancelEdit()
          }
        }}
        onBlur={() => commitEdit()}
        sx={{
          ...ui.input,
          width: def?.width || '100%',
          minWidth: def.valueMinWidth,
          '& .MuiInputBase-input': {
            fontSize: '0.95rem',
            color: getTextColor(),
            cursor: def?.readOnly ? 'default' : 'text',
          },
          '& input[type=number]': { MozAppearance: 'textfield' },
          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
      />
    )
  }

  const renderItem = (def, resolvedLabelWidth, applyUnitsWidthForSegment) => {
    const unitsKey = (def?.units ?? '').toString().trim()

    let unitsText = unitsKey ? getUnitsText(UNITS_FOR[unitsKey]) : ''
    const hasUnits = !!unitsText

    const unitsComponent = () => {
      if (!applyUnitsWidthForSegment) return null
      if (!hasUnits) return <Box sx={{ width: unitsWidth, minWidth: unitsWidth }} />
      return <Typography sx={{ ...ui.units, width: unitsWidth, minWidth: unitsWidth }}>{unitsText}</Typography>
    }

    const key = `${def?.group}-${def?.section}-${def?.subSection}-${def?.subSection2}-${def?.tag}`

    if (def?.type === 'divider') {
      return (
        <Box
          key={key}
          sx={{
            gridColumn: '1 / -1',
            width: '100%',
            borderBottom: `1px solid ${ui.titleDividerTrack.background}`,
            my: 0.5,
          }}
        />
      )
    }

    const isFullWidth = def?.type === 'longstring' || def?.width === '100%'

    return (
      <Box
        key={key}
        sx={{
          ...(def?.borderColor && {
            border: `2px solid ${def.borderColor}`,
            borderRadius: 1,
            p: 1,
          }),
        }}>
        <DataConfigFormItem
          key={key}
          item={def}
          reverse={def?.reverse}
          labelWidth={def?.labelWidth ?? resolvedLabelWidth}
          justifyContent={def?.labelJustify || undefined}
          controlMaxWidth={isFullWidth ? null : editorMaxWidth}
          sx={isFullWidth ? { gridColumn: '1 / -1' } : {}}
          extra={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{unitsComponent()}</Box>}>
          {renderValueCell(def)}
        </DataConfigFormItem>
      </Box>
    )
  }

  const content = (
    <>
      <Box
        onClick={collapsible ? () => setCollapsed((prev) => !prev) : undefined}
        sx={{
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: collapsible ? 'none' : undefined,
        }}>
        <GlassTitle
          title={title}
          ui={ui}
          icon={icon}
          trailingIcon={
            collapsible && (
              <Iconify
                icon='mdi:chevron-down'
                style={{
                  transition: 'transform 0.25s ease',
                  transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  color: ui.defaultTextColor,
                  fontSize: 20,
                }}
              />
            )
          }
        />
      </Box>
      <Collapse in={!collapsed} timeout={250}>
        {segmentedGroups.map((group, gi) => {
          const { numCols: gCols, labelWidth: gLW, display: gDisplay } = resolveSegment(group.key)
          const gApplyUnitsWidth = segmentHasUnits(group.defs)

          return (
            <Box
              key={`${group.key}-${gi}`}
              sx={{
                display: gDisplay === 'flex' ? 'flex' : gDisplay,
                flexWrap: gDisplay === 'flex' ? 'wrap' : undefined,
                alignItems: gDisplay === 'flex' ? 'flex-start' : undefined,
                gap: gDisplay === 'flex' ? 2 : 1,
                width: '100%',
                gridTemplateColumns:
                  gDisplay === 'grid' ? { xs: '1fr', md: `repeat(${gCols}, minmax(0, 1fr))` } : undefined,
                '& > *': gDisplay === 'flex' ? { flex: '0 0 auto' } : undefined,
              }}>
              {group.defs.map((def) => renderItem(def, gLW, gApplyUnitsWidth))}
            </Box>
          )
        })}
      </Collapse>
    </>
  )

  if (!withSurface) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', flex: 1, minWidth: '600px' }}>
        {content}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        ...ui.surface,
        ...(collapsible && { flex: 'none' }),
        minWidth: minWidth || `${numCols * 300}px`,
        ...(borderColor && {
          border: `2px solid ${borderColor}`,
        }),
      }}>
      {content}
    </Box>
  )
}

export default DataConfigParameterDisplay
